import React, { useState, useEffect, useCallback } from 'react';
import { Table, Avatar, Tag, Spin, Alert } from 'antd';
import { UserOutlined, CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../Firebase';
import { onAuthStateChanged, signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';

import './Leaderboard.css';

// Component สำหรับหน้ากระดานผู้นำ
function Leaderboard({ userData }) { // รับ userData จาก App.js
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null); // ใช้สำหรับ Firebase Auth UID
  const [isAuthReady, setIsAuthReady] = useState(false); // สถานะพร้อมใช้งาน Auth
  const [error, setError] = useState(null);

  // ดึง __app_id จาก global scope (สำหรับ Canvas environment)
  const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id';

  // Effect สำหรับจัดการสถานะ Firebase Authentication
  useEffect(() => {
    let unsubscribeAuth = () => {};
    if (auth) {
      unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        if (user) {
          // หากผู้ใช้ Login อยู่ (ไม่ว่าจะเป็น Anonymous หรือ Custom Token)
          setUserId(user.uid);
        } else {
          try {
            // พยายามเข้าสู่ระบบด้วย Custom Token ที่ให้มา หากไม่มี ให้ Login แบบ Anonymous
            const initialAuthToken = typeof window.__initial_auth_token !== 'undefined' ? window.__initial_auth_token : null;
            if (initialAuthToken) {
                await signInWithCustomToken(auth, initialAuthToken);
                setUserId(auth.currentUser?.uid);
            } else {
                await signInAnonymously(auth);
                setUserId(auth.currentUser?.uid);
            }
          } catch (anonSignInError) {
            // จัดการข้อผิดพลาดในการ Auth
            setError(`Authentication Error: ${anonSignInError.message}. Data might not be available.`);
            setUserId(`anon-error-${crypto.randomUUID()}`); // กำหนด ID ชั่วคราวเมื่อเกิดข้อผิดพลาด
          }
        }
        setIsAuthReady(true); // ตั้งค่า Auth เป็นพร้อมใช้งาน
      });
    } else {
        setError("Firebase Auth not initialized. Cannot fetch user data.");
        setIsAuthReady(true);
        setLoading(false);
    }

    // Cleanup function: ยกเลิกการ subscribe เมื่อ Component Unmount
    return () => {
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
    };
  }, [auth]); // Dependency array: auth instance

  // ฟังก์ชันสำหรับดึงข้อมูล Leaderboard จาก Firestore
  const fetchLeaderboardData = useCallback(async () => {
    // ไม่ดึงข้อมูลหาก Firestore instance ไม่พร้อมหรือ Auth ยังไม่พร้อม
    if (!db || !isAuthReady) {
      return;
    }
    setLoading(true); // เริ่มโหลดข้อมูล
    setError(null); // ล้างข้อผิดพลาดก่อนหน้า
    try {
      // ดึงข้อมูลจาก Collection "assumption"
      const usersCollectionRef = collection(db, "assumption"); 
      const querySnapshot = await getDocs(usersCollectionRef);
      const fetchedUsers = [];
      querySnapshot.forEach((document) => {
        const data = document.data();
        // ตรวจสอบว่าข้อมูลมี username, level, score และเป็นประเภทที่ถูกต้อง
        if (data.username && typeof data.level === 'number' && typeof data.score === 'number') {
          fetchedUsers.push({
            id: document.id,
            name: data.username,
            level: data.level,
            score: data.score,
            // เพิ่ม isCurrentUser เพื่อระบุแถวของผู้ใช้ปัจจุบัน
            isCurrentUser: userData && userData.username === data.username // ตรวจสอบ username
          });
        }
      });

      if (fetchedUsers.length === 0) {
        setLeaderboardData([]); // ไม่มีข้อมูล
      } else {
        // จัดเรียงข้อมูล: Score มากที่สุดก่อน, หาก Score เท่ากัน Level น้อยที่สุดก่อน
        fetchedUsers.sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return a.level - b.level; // ตรวจสอบ Level (เดิมเป็น a.level - b.level ซึ่งหมายถึง Level น้อยกว่าจะอยู่สูงกว่า)
        });

        // กำหนดอันดับ (Ranking) ให้กับแต่ละผู้ใช้
        const rankedData = fetchedUsers.map((user, index) => ({
          ...user,
          ranking: index + 1,
        }));

        setLeaderboardData(rankedData);
      }
    } catch (err) {
      // จัดการข้อผิดพลาดในการดึงข้อมูล
      if (err.code === 'permission-denied') {
        setError("Permission Denied: ตรวจสอบ Firestore Security Rules ของคุณ");
      } else {
        setError(`Error fetching data: ${err.message}. ตรวจสอบ Collection Path หรือการเชื่อมต่อ.`);
      }
      setLeaderboardData([]); // ล้างข้อมูลหากเกิดข้อผิดพลาด
    } finally {
      setLoading(false); // หยุดสถานะโหลด
    }
  }, [db, isAuthReady, userData]); // เพิ่ม userData ใน dependency array เพื่อให้ re-fetch เมื่อข้อมูลผู้ใช้เปลี่ยน

  // Effect สำหรับเรียก fetchLeaderboardData เมื่อ db หรือ Auth พร้อม
  useEffect(() => {
    if (db && isAuthReady) {
      fetchLeaderboardData();
    }
  }, [db, isAuthReady, fetchLeaderboardData]);

  // ฟังก์ชันสำหรับกลับไปยังหน้า Exercies
  const handleBackToExercies = () => {
    navigate('/exercies');
  };

  // คอลัมน์สำหรับ Ant Design Table
  const columns = [
    {
      title: 'Rank',
      dataIndex: 'ranking',
      key: 'ranking',
      align: 'center',
      render: (text) => {
        // แสดงไอคอนมงกุฎสำหรับ Top 3
        if (text === 1) {
          return (
            <div className="rank-cell gold"> {/* เพิ่มคลาส gold สำหรับสี */}
              <CrownOutlined className="crown-icon" /> {text}
            </div>
          );
        } else if (text === 2) {
          return (
            <div className="rank-cell silver">
              <CrownOutlined className="crown-icon" /> {text}
            </div>
          );
        } else if (text === 3) {
          return (
            <div className="rank-cell bronze">
              <CrownOutlined className="crown-icon" /> {text}
            </div>
          );
        }
        return <div className="rank-cell">{text}</div>;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div className="name-cell">
          <Avatar icon={<UserOutlined />} className="avatar" />
          {text}
        </div>
      ),
    },
    {
      title: 'Step', // เปลี่ยน "Level" เป็น "Step"
      dataIndex: 'level',
      key: 'level',
      align: 'center',
      render: (text) => {
        let color = 'geekblue'; // สีเริ่มต้น
        if (text >= 90) {
          color = 'volcano'; // ระดับสูงมาก
        } else if (text >= 80) {
          color = 'gold'; // ระดับสูง
        } else if (text >= 70) {
          color = 'lime'; // ระดับปานกลาง
        }
        return <Tag color={color} className="step-tag">{text}</Tag>;
      },
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (text) => (
        <span className="score-cell">
          {text.toLocaleString()} Points {/* แสดงผลคะแนนพร้อมข้อความ "Points" */}
        </span>
      ),
    },
  ];

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">

        {/* โลโก้อยู่ด้านขวา */}
        <div className="logo-container">
          <img 
            src="/Wisdom-Logo.png" 
            alt="Wisdom Logo" 
            className="logo-image1"
          />
        </div>

        {/* ข้อความอยู่ตรงกลาง */}
        <h1 className="leaderboard-title">
          Leaderboard
        </h1>
        
        {/* ปุ่ม "กลับ" อยู่ด้านซ้าย */}
        <button onClick={handleBackToExercies} className="back-button">
          Back
        </button>        

      </div>

      {error && (
        <Alert
          message="ข้อผิดพลาด"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>กำลังโหลด Leaderboard...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={leaderboardData}
          rowKey="id"
          pagination={false}
          bordered
          rowClassName={(record) => {
            // เพิ่มคลาสสำหรับไฮไลต์ผู้ใช้ปัจจุบัน
            let className = '';
            if (record.ranking === 1) className += ' rank-1-row';
            if (record.ranking === 2) className += ' rank-2-row';
            if (record.ranking === 3) className += ' rank-3-row';
            if (record.isCurrentUser) className += ' current-user-row'; // <-- เพิ่มบรรทัดนี้
            return className.trim();
          }}
          locale={{ emptyText: 'ไม่มีข้อมูลใน Leaderboard' }}
          className="leaderboard-table"
        />
      )}
      {userId && ( // แสดง userId เพื่อการดีบักหรือข้อมูล
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.9em', color: '#666' }}>
        {/*  Your Firebase Auth ID: {userId} */}
          {userData?.username && (
            <p>Logged in as: {userData.username}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Leaderboard;

